import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight } from 'lucide-react';

interface ImageStackProps {
  images: {
    src: string;
    alt: string;
  }[];
}

const ImageStack: React.FC<ImageStackProps> = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleClick = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  return (
    <div 
      className="relative h-[250px] sm:h-[500px] lg:h-[550px] w-full cursor-pointer"
      onClick={handleClick}
      style={{ 
        perspective: '1000px',
        transformStyle: 'preserve-3d',
        isolation: 'isolate'
      }}
    >
      <AnimatePresence mode="popLayout">
        {images.map((image, index) => {
          const isActive = index === currentIndex;
          const position = (index - currentIndex + images.length) % images.length;
          
          return (
            <motion.div
              key={image.src}
              className="absolute w-[70%] aspect-video rounded-2xl overflow-hidden"
              initial={false}
              animate={{
                x: getPositionX(position),
                y: getPositionY(position),
                scale: isActive ? 1 : 0.85,
                rotateY: position * -8,
                zIndex: isActive ? 30 : 10 - position,
                filter: isActive ? 'none' : 'brightness(0.5) blur(1px)',
                opacity: isActive ? 1 : 0.6,
              }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 30,
                mass: 1,
              }}
              style={{
                position: 'absolute',
                transformOrigin: 'center center -50px',
                boxShadow: isActive 
                  ? '0 20px 40px -5px rgba(0, 0, 0, 0.3)' 
                  : '0 10px 20px -5px rgba(0, 0, 0, 0.2)',
                isolation: 'isolate',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                transform: `translateZ(${isActive ? 0 : -position * 50}px)`,
                backgroundColor: '#fff',
                willChange: 'transform, opacity',
                transformStyle: 'preserve-3d',
                mixBlendMode: 'normal'
              }}
            >
              <motion.div
                className="relative w-full h-full group"
                whileHover={{ scale: isActive ? 1.02 : 1 }}
                transition={{ duration: 0.3 }}
                style={{
                  isolation: 'isolate',
                  position: 'relative',
                  zIndex: isActive ? 2 : 1,
                  transformStyle: 'preserve-3d',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden'
                }}
              >
                <img 
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover"
                  style={{ 
                    position: 'relative',
                    zIndex: 1,
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden',
                    transform: 'translateZ(0)',
                    pointerEvents: 'none'
                  }}
                />
                {isActive && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 flex items-center justify-center"
                    style={{ 
                      position: 'absolute',
                      zIndex: 2,
                      transform: 'translateZ(1px)',
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      isolation: 'isolate'
                    }}
                  >
                    <motion.div 
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm w-12 h-12 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      initial={{ x: -10 }}
                      animate={{ x: 0 }}
                      whileHover={{ scale: 1.1 }}
                    >
                      <ChevronRight className="w-6 h-6 text-success animate-pulse" />
                    </motion.div>
                    <motion.div 
                      className="absolute left-4 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full text-sm text-success/80 font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      initial={{ y: 10 }}
                      animate={{ y: 0 }}
                    >
                      {`${currentIndex + 1} / ${images.length}`}
                    </motion.div>
                  </motion.div>
                )}
              </motion.div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

const getPositionX = (position: number): string => {
  switch (position) {
    case 0: // Active
      return '15%';
    case 1: // Next
      return '0%';
    default: // Previous
      return '30%';
  }
};

const getPositionY = (position: number): string => {
  switch (position) {
    case 0: // Active
      return '50%';
    case 1: // Next
      return '15%';
    default: // Previous
      return '30%';
  }
};

export default ImageStack;